import { apiClient } from './client';
import { 
  ApiNotification, 
  NotificationAlert, 
  NotificationFilters, 
  NotificationResponse, 
  CreateAlertRequest,
} from '../../types/notification';

export class NotificationService {
  private apiClient = apiClient;
  private sseConnection: EventSource | null = null;
  private notificationCallbacks: ((notification: ApiNotification) => void)[] = [];

  /**
   * Get user notifications with filtering and pagination
   */
  async getNotifications(filters: NotificationFilters = {}): Promise<NotificationResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.read !== undefined) queryParams.append('read', filters.read.toString());
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.from) queryParams.append('from', filters.from);
      if (filters.to) queryParams.append('to', filters.to);
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());

      const data = await this.apiClient.get<NotificationResponse>(`/v1/notifications?${queryParams.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Create a new notification alert
   */
  async createAlert(alertRequest: CreateAlertRequest): Promise<NotificationAlert> {
    try {
      const data = await this.apiClient.post<NotificationAlert>('/v1/notifications', alertRequest);
      return data;
    } catch (error) {
      console.error('Error creating notification alert:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<{ id: string; read: boolean; updatedAt: string }> {
    try {
      const data = await this.apiClient.put<{ id: string; read: boolean; updatedAt: string }>(`/v1/notifications/${notificationId}/read`, {});
      return data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; count: number; updatedAt: string }> {
    try {
      const data = await this.apiClient.put<{ success: boolean; count: number; updatedAt: string }>('/v1/notifications/read-all', {});
      return data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<{ deleted: boolean; id: string }> {
    try {
      const data = await this.apiClient.delete<{ deleted: boolean; id: string }>(`/v1/notifications/${notificationId}`);
      return data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Delete a notification alert
   */
  async deleteAlert(alertId: string): Promise<{ deleted: boolean; id: string }> {
    try {
      const data = await this.apiClient.delete<{ deleted: boolean; id: string }>(`/v1/notifications/alerts/${alertId}`);
      return data;
    } catch (error) {
      console.error('Error deleting notification alert:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time notifications via SSE
   */
  subscribeToNotifications(callback: (notification: ApiNotification) => void): void {
    this.notificationCallbacks.push(callback);

    if (!this.sseConnection) {
      this.connectToNotificationStream();
    }
  }

  /**
   * Unsubscribe from real-time notifications
   */
  unsubscribeFromNotifications(callback: (notification: ApiNotification) => void): void {
    const index = this.notificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.notificationCallbacks.splice(index, 1);
    }

    // Close SSE connection if no more callbacks
    if (this.notificationCallbacks.length === 0 && this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }
  }

  /**
   * Establish SSE connection for real-time notifications
   */
  private connectToNotificationStream(): void {
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (!token) {
        console.error('No session token available for notification SSE');
        return;
      }

      // Create SSE connection for notifications
      const baseUrl = 'https://aimock.mobile-bot.deriv.dev';
      const sseUrl = `${baseUrl}/v1/stream/connect?channels=notifications`;

      this.sseConnection = new EventSource(sseUrl);

      // Set authorization header (EventSource doesn't support custom headers, 
      // so we'll need to pass token as query parameter or use fetch SSE)
      this.setupFetchSSE(sseUrl, token);

    } catch (error) {
      console.error('Error connecting to notification stream:', error);
    }
  }

  /**
   * Setup fetch-based SSE with custom headers for notifications
   */
  private async setupFetchSSE(url: string, token: string): Promise<void> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get SSE stream reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: notification')) {
            // Next line should contain the data
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const eventData = line.slice(6); // Remove 'data: ' prefix
              const notification: ApiNotification = JSON.parse(eventData);
              
              // Notify all callbacks
              this.notificationCallbacks.forEach(callback => {
                callback(notification);
              });
            } catch (parseError) {
              console.error('Error parsing notification SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in fetch SSE for notifications:', error);
      
      // Retry connection after 5 seconds
      setTimeout(() => {
        if (this.notificationCallbacks.length > 0) {
          this.connectToNotificationStream();
        }
      }, 5000);
    }
  }

  /**
   * Create a price alert notification
   */
  async createPriceAlert(instrumentId: string, condition: 'above' | 'below', targetPrice: number, expiryTime?: string): Promise<NotificationAlert> {
    const alertRequest: CreateAlertRequest = {
      type: 'price_alert',
      instrumentId,
      condition,
      targetPrice,
      expiryTime
    };

    return this.createAlert(alertRequest);
  }

  /**
   * Create a balance threshold alert
   */
  async createBalanceAlert(condition: 'above' | 'below', threshold: number): Promise<NotificationAlert> {
    const alertRequest: CreateAlertRequest = {
      type: 'balance_threshold',
      condition,
      threshold
    };

    return this.createAlert(alertRequest);
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await this.getNotifications({ read: false, limit: 1 });
      return response.unreadCount;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Format notification for display
   */
  formatNotificationForUI(notification: ApiNotification) {
    const formatTime = (timestamp: string) => {
      const now = new Date();
      const notificationTime = new Date(timestamp);
      const diffMs = now.getTime() - notificationTime.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0) {
        return `${diffDays}d ago`;
      } else if (diffHours > 0) {
        return `${diffHours}h ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes}m ago`;
      } else {
        return 'Just now';
      }
    };

    const getIcon = (type: string) => {
      switch (type) {
        case 'position_expiring': return '⏰';
        case 'price_alert': return '📈';
        case 'trade_result': return '💰';
        case 'balance_update': return '💳';
        case 'news': return '📰';
        case 'system': return '⚙️';
        default: return '🔔';
      }
    };

    const getPriority = (type: string) => {
      switch (type) {
        case 'position_expiring': return 'high';
        case 'trade_result': return 'high';
        case 'price_alert': return 'medium';
        case 'balance_update': return 'medium';
        case 'system': return 'low';
        case 'news': return 'low';
        default: return 'medium';
      }
    };

    return {
      ...notification,
      formattedTime: formatTime(notification.createdAt),
      icon: getIcon(notification.type),
      priority: getPriority(notification.type),
      actionable: ['position_expiring', 'price_alert'].includes(notification.type)
    };
  }

  /**
   * Disconnect from notification stream
   */
  disconnect(): void {
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }
    this.notificationCallbacks = [];
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
